import React, { useContext, useEffect } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import cn from 'classnames';
import Scroll, { scroller } from 'react-scroll';
import LazyLoad from 'react-lazyload';
import { Helmet } from 'react-helmet';
import ConfigContext from '../../../contexts/ConfigContext';
import { useGetPublicTruckByIdQuery } from '../../../redux/slices/truckSlice';
import { intersperse } from '../../../modules/helpers';

// Components
import TruckHero from './TruckHero';
import PublicEvents from '../events/PublicEvents';
import Rating from '../Rating';
import Meta from '../../shared/Meta';
import DisqusReviews from '../content/DisqusReviews';
import TruckFaq from './TruckFaq';
import ScrollNav from '../ScrollNav';
import TruckSocial from './TruckSocial';
import TruckHighlights from './TruckHighlights';
import TruckInstagram from './TruckInstagram';
import TruckMenu from './TruckMenu';
import FeaturedTrucks from '../FeaturedTrucks';
import SimpleCta from '../content/SimpleCta';
import CateringCta from '../content/CateringCta';
import TruckSidebar from './TruckSidebar';
import Loader from '../../shared/Loader';

import sidebarStyles from '../../../../stylesheets/public/layouts/SidebarLayout.module.postcss';
import styles from './Truck.module.postcss';

const OFFSET = -100;

function Truck() {
  const { imageBase, vehicle_type, site_title, support_email, base_url, city_name } = useContext(ConfigContext);
  const { id } = useParams();
  const { hash } = useLocation();
  const { data: truck = {}, isLoading } = useGetPublicTruckByIdQuery(id);

  // Scroll to menu if URL has #menu
  useEffect(() => {
    if (hash === '#menu') {
      scroller.scrollTo('menu', {
        offset: OFFSET,
      });
    }
  }, [hash, truck]);

  let featuredTrucks = {};
  if (truck && truck.food_categories) {
    featuredTrucks = {
      food_categories: truck.food_categories.map((cat) => cat.id),
      exclude_truck: truck.uid
    };
  }

  let categories = null;
  let categoriesString = null;
  if (truck && truck.food_categories) {
    categories = truck.food_categories.map((cat) => {
      return (
        <Link key={cat.id} to={`/search?query=${cat.id}`}>{cat.name}</Link>
      );
    });
    categories = intersperse(categories, <span aria-hidden='true'>, </span>);
    categoriesString = truck.food_categories.map((category) => category.name).join(', ');
  }

  let photos = null;
  if (truck && truck.photos) {
    photos = truck.photos.filter((p) => p.category !== 'logo');
    photos = photos.map((photo) => {
      return `${imageBase}/${photo.file}`;
    });
  }

  let vehicleType = 'Food Truck';
  if (truck && truck.cart) { vehicleType = 'Food Cart'; }
  if (truck && truck.trailer) { vehicleType = 'Food Trailer'; }

  let title = 'Food ' + vehicle_type;
  if (truck.name) {
    title = `${truck.name} ${vehicleType} Schedule`;
  }

  const links = [
    { title: 'About', to: 'about' },
    ...(truck.menu_items && truck.menu_items.length > 0 ? [{ title: 'Menu', to: 'menu' }] : []),
    { title: 'Schedule', to: 'schedule' },
    { title: 'Reviews', to: 'reviews' },
  ];

  // Build structured data for this truck
  const truckStructuredData = {};
  if (truck) {
    truckStructuredData.breadcrumbs = {
      listItems: [
        { '@type': 'ListItem', position: 2, name: 'Food Trucks', item: `${base_url}/explore` },
        { '@type': 'ListItem', position: 3, name: truck.name, item: `${base_url}/food-trucks/${id}` },
      ],
    };
    truckStructuredData.foodEstablishment = {
      name: truck.name,
      url: `${base_url}/food-trucks/${id}`,
      image: truck.featured_photo,
      servesCuisine: categoriesString,
      addressLocality: city_name,
      ...(truck.phone && { telephone: truck.phone }),
      ...(truck.yelp_rating && truck.yelp_rating_count && {
        aggregateRating: {
          ratingValue: truck.yelp_rating,
          reviewCount: truck.yelp_rating_count,
        },
      }),
    };
  }

  return (
    <div>
      <Meta
        title={title}
        structuredData={truck.name ? truckStructuredData : undefined}
        breadcrumbName={truck.name ? `${truck.name} Food ${vehicle_type}` : undefined}
        type='website'
        image={truck.featured_photo}
        description={truck.description}
        canonical={`/food-trucks/${id}`}
      />

      { photos && photos.length > 0 &&
        <Helmet>
          <link rel="preload" as="image" href={photos[0]} fetchpriority="high" />
        </Helmet>
      }

      <TruckHero photos={photos} alt={truck.name} />

      <ScrollNav links={links} offset={OFFSET} />

      <div className={'Section Section--extraPadding ' + styles.TruckDetail}>
        <div className={sidebarStyles.SidebarLayout}>
          <div className={styles.TruckDetail__SidebarLayout_body}>
            <Loader isLoading={isLoading || !truck} padded>

              <div className={styles.TruckDetail_header}>
                <div>
                  <h1>{truck.name}</h1>

                  { categories && <div className={styles.TruckDetail_categories}>{categories}</div> }

                  <div className={styles.TruckDetail_social + ' u-flex'}>
                    <Rating
                      rating={truck.yelp_rating}
                      ratingCount={truck.yelp_rating_count}
                      yelp={truck.yelp}
                    />
                    <TruckSocial
                      facebook={truck.facebook}
                      twitter={truck.twitter}
                      instagram={truck.instagram}
                      yelp={truck.yelp}
                      rating={truck.yelp_rating}
                    />
                  </div>
                </div>

                { truck.logo &&
                  <div
                    className={styles.TruckDetail_headerImg}
                    style={{ backgroundImage: `url("${imageBase}/${truck.logo}")` }}
                  />
                }
              </div>

              <TruckHighlights truck={truck} />

              <Scroll.Element name='about' className='InnerSection'>
                <h2>About</h2>
                <div className={styles.TruckDetail_description}>
                  {truck.description}
                </div>
              </Scroll.Element>

              <TruckInstagram feed={truck.instagram_feed} />

              { truck.menu_items && truck.menu_items.length > 0 &&
                <Scroll.Element name='menu' className='InnerSection'>
                  <h2 className='u-mb05'>Menu</h2>
                  <TruckMenu truck={truck} />
                </Scroll.Element>
              }

              <Scroll.Element name='schedule' className='InnerSection'>
                <PublicEvents
                  truckId={truck.id}
                  description={truck.description}
                  image={truck.featured_photo}
                />
              </Scroll.Element>

              <Scroll.Element name='reviews' className='InnerSection'>
                <h2>Customer reviews</h2>
                <LazyLoad height={500} offset={100} once>
                  <DisqusReviews truck={truck} />
                </LazyLoad>
              </Scroll.Element>

              <div className='InnerSection'>
                <TruckFaq truck={truck} />
              </div>

              <div className='InnerSection'>
                <LazyLoad height={500} offset={100} once>
                  <FeaturedTrucks
                    title={`More ${vehicleType.toLowerCase()}s like ${truck.name}`}
                    options={featuredTrucks}
                    noWrapper
                  />
                </LazyLoad>
              </div>

              <hr className='u-mb0' />

              <div className='InnerSection'>
                <SimpleCta
                  text='See something wrong on this page? Let us know.'
                  link={'mailto:' + support_email}
                  linkTitle='Send feedback'
                  linkIsExternal
                />
              </div>

              <div className='InnerSection'>
                <CateringCta />
              </div>

            </Loader>

          </div>

          <div className={cn(sidebarStyles.SidebarLayout_sidebar, sidebarStyles.SidebarLayout_sidebar__noMobile)}>
            <Loader isLoading={isLoading || !truck}>
              <TruckSidebar truckName={truck.name} />
            </Loader>
          </div>
        </div>
      </div>

    </div>
  );
}

export default Truck;
